import type { SettlementDetail } from '@services/settlements/types'
import { ArrowLeft, ArrowRight, Check } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

import { SettlementAdvancesCard } from '../SettlementAdvancesCard'
import { SettlementCostItemsCard } from '../SettlementCostItemsCard'
import { absoluteAmount, isUnderpayment, money, months } from '../format'
import { StepElectricity } from './StepElectricity'
import { StepHeat } from './StepHeat'
import { StepPeriod } from './StepPeriod'
import { StepSummary } from './StepSummary'
import { StepWater } from './StepWater'
import { STEPS, type StepId, stepSeverities } from './steps'
import { useSettlementInputsForm } from './useSettlementInputsForm'

export function SettlementWizard({
  settlement
}: {
  settlement: SettlementDetail
}) {
  const { t } = useTranslation('settlement')
  const { form, save, isSaving } = useSettlementInputsForm(settlement)
  const [index, setIndex] = useState(0)

  const readOnly = settlement.status === 'PUBLISHED'
  const severities = stepSeverities(settlement.checks)
  const step = STEPS[index] ?? STEPS[0]!

  const goTo = async (next: number) => {
    await save()
    setIndex(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const panels: Record<StepId, React.ReactNode> = {
    period: <StepPeriod form={form} settlement={settlement} />,
    water: (
      <StepWater settlement={settlement} form={form} readOnly={readOnly} />
    ),
    heat: <StepHeat form={form} />,
    other: <SettlementCostItemsCard settlement={settlement} readOnly={readOnly} embedded />,
    electricity: (
      <StepElectricity
        settlement={settlement}
        form={form}
        readOnly={readOnly}
      />
    ),
    advances: (
      <SettlementAdvancesCard settlement={settlement} readOnly={readOnly} embedded />
    ),
    summary: (
      <StepSummary settlement={settlement} form={form} readOnly={readOnly} />
    )
  }

  const total = settlement.result.grand_total
  const underpayment = isUnderpayment(total)

  // Show the unit the calculation actually uses. Printing days while the
  // annual amounts are split by months invites exactly the question of why
  // the two do not line up.
  const byDays = settlement.proration_base === 'DAYS'
  const scale = byDays
    ? t('result.period_days', { count: settlement.result.period_days })
    : t('result.period_months', {
        months: months(settlement.result.proration_share)
      })

  return (
    <div className="space-y-4">
      {/* Step rail */}
      <div className="-mx-1 overflow-x-auto px-1">
        <div className="inline-flex items-center gap-1">
          {STEPS.map((current, position) => {
            const active = position === index
            const severity = severities[current.id]

            return (
              <button
                key={current.id}
                type="button"
                onClick={() => goTo(position)}
                className={cn(
                  'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors',
                  active
                    ? 'border-primary bg-primary/10 text-ink'
                    : 'border-ink/[0.08] bg-transparent text-muted-foreground hover:text-ink'
                )}
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ink/[0.06] text-[11px] tabular-nums">
                  {position + 1}
                </span>
                <current.icon className="h-4 w-4" />
                {t(`wizard.${current.id}.title`)}
                {severity && (
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full',
                      severity === 'ERROR' ? 'bg-destructive' : 'bg-warning'
                    )}
                  />
                )}
              </button>
            )
          })}
        </div>
      </div>

      <Card className="p-5 sm:p-6">
        <h2 className="m-0 mb-1 font-display text-xl font-bold tracking-tight text-ink">
          {t(`wizard.${step.id}.title`)}
        </h2>
        <p className="m-0 mb-5 text-sm text-muted-foreground">
          {t('wizard.step_of', { current: index + 1, total: STEPS.length })}
          {step.optional && ` · ${t('wizard.optional')}`}
        </p>

        {panels[step.id]}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-ink/[0.08] pt-4">
          <Button
            type="button"
            variant="outline"
            disabled={index === 0}
            onClick={() => goTo(index - 1)}
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {t('wizard.back')}
          </Button>

          <div className="flex items-center gap-3">
            {isSaving && (
              <span className="text-sm text-muted-foreground">
                {t('wizard.saving')}
              </span>
            )}
            {index < STEPS.length - 1 ? (
              <Button type="button" onClick={() => goTo(index + 1)}>
                {step.optional ? t('wizard.skip') : t('wizard.next')}
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={() => save()}>
                <Check className="mr-1.5 h-4 w-4" />
                {t('actions.save')}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Running total — always visible, so every entered number shows its
          effect immediately instead of at the end. */}
      <div className="sticky bottom-4 z-10">
        <Card className="flex flex-wrap items-center justify-between gap-3 border-primary/30 p-4 shadow-lg">
          <span className="text-sm text-muted-foreground">
            {t('result.grand_total')} · {scale}
          </span>
          <span
            className={cn(
              'font-display text-lg font-bold tabular-nums',
              underpayment ? 'text-destructive' : 'text-success'
            )}
          >
            {underpayment
              ? t('result.underpayment')
              : t('result.overpayment')}{' '}
            {money(absoluteAmount(total))}
          </span>
        </Card>
      </div>
    </div>
  )
}
